/**
 * rounds.js - choosing what the child is asked for this time.
 *
 * PLATFORM SCAFFOLD. Section 8.5's content rules, in general form: draw from a
 * pool, avoid what was just used, move the positions about. The pools and the
 * vocabularies stay with the game.
 *
 * PURE, and deterministic given a seed: every test can replay a session
 * exactly, and a therapist can be told which seed a session ran on.
 *
 * ---------------------------------------------------------------------------
 * SECTION 8.5 IS A CONTENT RULE, NOT A DECORATION
 *
 * "Vary scenarios, object combinations and positions. Randomise within the
 * structure of each level." A level is a SKILL; the words are an instance of
 * it. Ten plays of Phase 1 Level 1 - the window the chart needs before a box
 * can go green - is ten prompts, and ten identical prompts is precisely the
 * predictable pattern section 2.3 calls very bad for this population.
 *
 * So: draw from the pool, never repeat what was just used while the pool can
 * afford not to, and move the positions about.
 *
 * ---------------------------------------------------------------------------
 * HOW LONG THE MEMORY HAS TO BE
 *
 * The first playtest ran fifteen attempts at Phase 1 Level 1 and the child saw
 * four words. Two reasons, and the pool was only one of them:
 *
 *   - the pool held four objects out of a catalogue of twenty, and
 *   - the memory was one round deep, so with a pool of four the third round
 *     could return to what the first one used.
 *
 * The pool is content and has been widened. The memory is here: MEMORY_ROUNDS
 * targets are avoided, not one, so a pool of eight cycles rather than
 * alternates. The fallback chain matters as much as the rule - a level whose
 * pool cannot honour the memory falls back to honouring the previous round,
 * and then to the whole pool, because an empty field is worse than a repeat.
 */

/** How many recent targets a draw avoids, pool permitting. */
export const MEMORY_ROUNDS = 4;

/**
 * How many things are on screen this round.
 *
 * A game names its field after whatever the child sees - bubbles, doors, cards
 * - and the planner does not care which, so all three spellings are read. What
 * it must never be is smaller than the number of targets.
 *
 * @param {object} level a content level entry
 * @returns {number}
 */
export function fieldSize(level) {
  const declared = level.fieldSize ?? level.bubblesOnScreen ?? level.doorsOnScreen;
  return Math.max(level.targetsPerRound, declared ?? level.targetsPerRound);
}

/**
 * Small, fast, seeded PRNG. Not cryptographic and does not need to be; it
 * needs to be reproducible, which Math.random() is not.
 * @param {number} seed
 * @returns {() => number} in [0, 1)
 */
export function createRng(seed = 1) {
  let a = (seed >>> 0) || 1;
  return function rng() {
    a += 0x6D2B79F5;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Fisher-Yates, on a copy. */
export function shuffle(items, rng) {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Pick one round.
 *
 * @param {object} input
 * @param {object} input.level      a content/levels.js entry
 * @param {object|null} [input.previous] the round before, so it can be avoided
 * @param {string[]} [input.recent] object ids used recently, oldest first, to
 *        be avoided as well - see MEMORY_ROUNDS
 * @param {() => number} input.rng
 * @returns {{targets:Array<{objectId:string, carrierId:string|null, slot:number}>,
 *            field:Array<{objectId:string, slot:number, isTarget:boolean}>,
 *            carrierId:string|null}}
 */
export function pickRound({ level, previous = null, recent = [], rng }) {
  const justUsed = new Set(
    (previous ? previous.targets : []).map((t) => t.objectId)
  );
  const remembered = new Set([...recent, ...justUsed]);

  // Three sources, best first. Prefer objects used in none of the remembered
  // rounds; failing that, at least not in the last one; failing that, the whole
  // pool - a repeat is better than an empty field, and the shuffle still moves
  // it about.
  const unseen = level.pool.filter((id) => !remembered.has(id));
  const notLast = level.pool.filter((id) => !justUsed.has(id));
  const source = unseen.length >= level.targetsPerRound
    ? unseen
    : (notLast.length >= level.targetsPerRound ? notLast : level.pool);

  const targets = shuffle(source, rng).slice(0, level.targetsPerRound);

  // Distractors: anything in the pool that is not a target this round.
  const rest = shuffle(level.pool.filter((id) => !targets.includes(id)), rng);
  const onScreen = targets.concat(rest).slice(0, fieldSize(level));

  // Positions are shuffled independently of the draw, so the target is not
  // reliably first - a child who learns "it is always the left one" has
  // learned the layout instead of the word.
  const slots = shuffle(onScreen.map((_, i) => i), rng);

  const carrierId = pickCarrier({ level, previous, rng });

  const field = onScreen.map((objectId, i) => ({
    objectId,
    slot: slots[i],
    isTarget: targets.includes(objectId),
  }));

  return {
    carrierId,
    field,
    targets: targets.map((objectId) => ({
      objectId,
      carrierId,
      slot: field.find((b) => b.objectId === objectId).slot,
      // Everything legally nameable this round. The trial record calls it
      // legalMoves and several content judgements need it.
      onScreen,
    })),
  };
}

/**
 * Phase 3's carrier for this round.
 *
 * With one carrier offered there is nothing to choose. With two, the round
 * picks which one Pip is hoping for - but BOTH remain legal answers, because
 * the level is asking the child to make a request, not to guess Pip's mind.
 * The game acts out whichever carrier the child actually used.
 */
function pickCarrier({ level, previous, rng }) {
  if (!level.carriers || !level.carriers.length) return null;
  if (level.carriers.length === 1) return level.carriers[0];

  const last = previous ? previous.carrierId : null;
  const options = level.carriers.filter((c) => c !== last);
  const from = options.length ? options : level.carriers;
  return from[Math.floor(rng() * from.length)];
}

/**
 * The rounds of one pass through a level, pre-rolled so the whole level can be
 * inspected (and tested) before a child sees any of it.
 *
 * @param {object} level
 * @param {() => number} rng
 * @param {object} [opts]
 * @param {object|null} [opts.previous] the last round of the PREVIOUS pass. A
 *        level is replayed until its boxes have ten plays each, and a memory
 *        that resets at every pass boundary is a memory with a seam in it -
 *        the child hears the same word twice across it.
 * @param {string[]} [opts.recent] object ids from before this pass.
 * @returns {Array<object>}
 */
export function planLevel(level, rng, opts = {}) {
  const rounds = [];
  let previous = opts.previous || null;
  const recent = [...(opts.recent || [])];

  for (let i = 0; i < level.rounds; i += 1) {
    previous = pickRound({ level, previous, recent, rng });
    rounds.push(previous);
    for (const target of previous.targets) recent.push(target.objectId);
    while (recent.length > MEMORY_ROUNDS) recent.shift();
  }
  return rounds;
}

/** The target ids of a plan's last MEMORY_ROUNDS draws, to carry into the next pass. */
export function recentFrom(rounds = []) {
  const ids = rounds.flatMap((round) => round.targets.map((t) => t.objectId));
  return ids.slice(-MEMORY_ROUNDS);
}

export default { createRng, shuffle, pickRound, planLevel, recentFrom, fieldSize, MEMORY_ROUNDS };
